import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { TutorialState, TutorialContextValue, SoftwareType, TutorialTask } from './types';
import { TutorialService } from './TutorialService';
import { getAllTutorialConfigs, getTutorialForUser } from './registry';

const TutorialContext = createContext<TutorialContextValue | null>(null);

const SESSION_COMPLETION_PREFIX = 'tutorialSessionCompleted:';
const PENDING_COMPLETION_PREFIX = 'tutorialPendingCompletion:';

const getSessionCompletionKey = (software: SoftwareType) =>
  `${SESSION_COMPLETION_PREFIX}${software}`;

const getPendingCompletionKey = (software: SoftwareType) =>
  `${PENDING_COMPLETION_PREFIX}${software}`;

const setSessionCompletionFlag = (software: SoftwareType) => {
  try {
    sessionStorage.setItem(getSessionCompletionKey(software), 'true');
  } catch (error) {
    console.warn(
      `[TutorialProvider] Unable to persist session completion for ${software}:`,
      error,
    );
  }
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: React.ReactNode;
  userId: string | null;
  userPermissions: string[];
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ 
  children, 
  userId,
  userPermissions 
}) => {
  const serviceRef = useRef<TutorialService | null>(null);

  const recordCompletion = useCallback((software: SoftwareType) => {
    setSessionCompletionFlag(software);

    if (userId) {
      try {
        const service =
          serviceRef.current && serviceRef.current.getConfig().software === software
            ? serviceRef.current
            : new TutorialService(software);
        service.markCompleted(userId);
        localStorage.removeItem(getPendingCompletionKey(software));
      } catch (error) {
        console.error(
          `[TutorialProvider] Failed to persist tutorial completion for ${software}:`,
          error,
        );
        localStorage.setItem(getPendingCompletionKey(software), 'true');
      }
      return;
    }

    localStorage.setItem(getPendingCompletionKey(software), 'true');
  }, [userId]);
  
  const [state, setState] = useState<TutorialState>({
    isActive: false,
    currentTaskIndex: 0,
    currentStepId: '',
    software: '',
    tasks: [],
    taskData: new Map(),
    loading: false,
    error: null,
  });

  const startTutorial = useCallback((software: SoftwareType) => {
    try {
      const service = new TutorialService(software);
      serviceRef.current = service;
      
      const tasks = service.getTasks();
      
      setState({
        isActive: true,
        currentTaskIndex: 0,
        currentStepId: service.getFirstStepForTask(0),
        software,
        tasks: tasks,
        taskData: new Map(),
        loading: false,
        error: null,
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: `Failed to start tutorial: ${error instanceof Error ? error.message : 'Unknown error'}`,
      }));
    }
  }, []);

  // Initialize tutorial service when software changes
  useEffect(() => {
    if (state.isActive && state.software) {
      try {
        serviceRef.current = new TutorialService(state.software);
        setState(prev => ({
          ...prev,
          tasks: serviceRef.current?.getTasks() || [],
          error: null,
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to initialize tutorial',
        }));
      }
    }
  }, [state.isActive, state.software]);

  // Check if user should see tutorial on mount
  // Don't include startTutorial in deps to avoid circular dependency
  useEffect(() => {
    if (userId && userPermissions.length > 0) {
      // Check if user has label permissions
      const hasLabelPermission = userPermissions.some(perm => perm.startsWith('label_'));
      
      // Only proceed with tutorial check if user has label permissions
      if (!hasLabelPermission) {
        return;
      }
      
      const tutorialConfig = getTutorialForUser(userPermissions);
      if (!tutorialConfig) {
        setState(prev => ({
          ...prev,
          error: 'No tutorial available for your current permissions',
        }));
        return;
      }

      let sessionCompleted = false;
      try {
        sessionCompleted =
          sessionStorage.getItem(getSessionCompletionKey(tutorialConfig.software)) === 'true';
      } catch (error) {
        console.warn(
          `[TutorialProvider] Unable to read session completion flag for ${tutorialConfig.software}:`,
          error,
        );
      }

      const pendingKey = getPendingCompletionKey(tutorialConfig.software);
      const hasPendingCompletion = localStorage.getItem(pendingKey) === 'true';

      let service: TutorialService;
      try {
        service = new TutorialService(tutorialConfig.software);
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: `Failed to initialize tutorial: ${error instanceof Error ? error.message : 'Unknown error'}`,
        }));
        return;
      }

      const isCompleted = service.isCompleted(userId);
      
      // Only start tutorial if not completed, not already active, and no pending completion marker
      if (!isCompleted && !sessionCompleted && !hasPendingCompletion && !state.isActive) {
        try {
          serviceRef.current = service;
          
          const tasks = service.getTasks();
          
          setState({
            isActive: true,
            currentTaskIndex: 0,
            currentStepId: service.getFirstStepForTask(0),
            software: tutorialConfig.software,
            tasks: tasks,
            taskData: new Map(),
            loading: false,
            error: null,
          });
        } catch (error) {
          setState(prev => ({
            ...prev,
            error: `Failed to start tutorial: ${error instanceof Error ? error.message : 'Unknown error'}`,
          }));
        }
      }
    }
  }, [userId, userPermissions?.join(',')]); // Use a stable dependency for permissions array

  const loadCurrentTaskData = useCallback(async () => {
    if (!serviceRef.current || !state.isActive) return;
    
    const currentTask = state.tasks[state.currentTaskIndex];
    if (!currentTask) return;

    // Check if we already have the data
    if (state.taskData.has(currentTask.id)) return;

    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const data = await serviceRef.current.fetchTaskData(currentTask);
      setState(prev => {
        const newTaskData = new Map(prev.taskData);
        newTaskData.set(currentTask.id, data);
        return {
          ...prev,
          taskData: newTaskData,
          loading: false,
        };
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load task data',
      }));
    }
  }, [state.isActive, state.currentTaskIndex, state.tasks, state.taskData]);

  // Load task data when task changes
  useEffect(() => {
    loadCurrentTaskData();
  }, [state.currentTaskIndex, loadCurrentTaskData]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    getAllTutorialConfigs().forEach((config) => {
      const pendingKey = getPendingCompletionKey(config.software);
      if (localStorage.getItem(pendingKey) !== 'true') {
        return;
      }

      try {
        const service =
          serviceRef.current && serviceRef.current.getConfig().software === config.software
            ? serviceRef.current
            : new TutorialService(config.software);
        service.markCompleted(userId);
        localStorage.removeItem(pendingKey);
        setSessionCompletionFlag(config.software);
      } catch (error) {
        console.error(
          `[TutorialProvider] Failed to finalize pending completion for ${config.software}:`,
          error,
        );
      }
    });
  }, [userId]);

  const nextTask = useCallback(() => {
    if (!serviceRef.current || state.currentTaskIndex >= state.tasks.length - 1) {
      console.log(`[TutorialProvider] nextTask: Cannot advance, currentIndex=${state.currentTaskIndex}, total tasks=${state.tasks.length}`);
      return;
    }
    
    const nextIndex = state.currentTaskIndex + 1;
    
    console.log(`[TutorialProvider] nextTask: Advancing from task ${state.currentTaskIndex} to ${nextIndex}`);
    
    // Only update the task index, let completeStep handle the step transition
    setState(prev => ({
      ...prev,
      currentTaskIndex: nextIndex,
    }));
  }, [state.currentTaskIndex, state.tasks]);

  const previousTask = useCallback(() => {
    if (!serviceRef.current || state.currentTaskIndex <= 0) return;
    
    const prevIndex = state.currentTaskIndex - 1;
    const firstStep = serviceRef.current.getFirstStepForTask(prevIndex);
    
    setState(prev => ({
      ...prev,
      currentTaskIndex: prevIndex,
      currentStepId: firstStep,
    }));
  }, [state.currentTaskIndex]);

  const completeStep = useCallback((stepId: string) => {
    if (!serviceRef.current) return;

    const config = serviceRef.current.getConfig();
    const currentIndex = config.steps.findIndex(s => s.id === stepId);
    const currentStep = config.steps[currentIndex];

    console.log(`[TutorialProvider] completeStep called`, {
      stepId,
      currentIndex,
      totalSteps: config.steps.length,
      currentScope: currentStep?.scope,
      currentTaskIndex: state.currentTaskIndex,
    });

    if (currentIndex >= 0 && currentIndex < config.steps.length - 1) {
      // Move to next step
      const nextStep = config.steps[currentIndex + 1];

      console.log(`[TutorialProvider] Next step details`, {
        nextStepId: nextStep.id,
        nextStepScope: nextStep.scope,
        nextStepTitle: nextStep.title,
        nextStepIndex: currentIndex + 1,
      });

      // Check if we're moving to a new task
      const currentTaskIndex = state.currentTaskIndex;
      let newTaskIndex = currentTaskIndex;

      // Check if the next step is an intro step for a new task by pattern matching
      const taskIntroMatch = nextStep.id.match(/^task-(\d+)-intro$/);

      if (taskIntroMatch) {
        // Extract task number from the step id (task-2-intro -> 2)
        const taskNumber = parseInt(taskIntroMatch[1], 10);
        // Task index is 0-based, so task-2-intro corresponds to index 1
        newTaskIndex = taskNumber - 1;
        console.log(`[TutorialProvider] Detected task transition to task ${newTaskIndex} (${nextStep.id})`);
      }

      console.log(`[TutorialProvider] Transitioning from step ${stepId} to ${nextStep.id}, taskIndex: ${currentTaskIndex} -> ${newTaskIndex}`);

      setState(prev => ({
        ...prev,
        currentStepId: nextStep.id,
        currentTaskIndex: newTaskIndex
      }));
    } else if (currentIndex === config.steps.length - 1) {
      // Last step - tutorial complete
      console.log(`[TutorialProvider] Last step reached, tutorial complete`);
    } else {
      console.warn(`[TutorialProvider] completeStep called with invalid index`, {
        stepId,
        currentIndex,
        totalSteps: config.steps.length,
      });
    }
  }, [state.currentTaskIndex]);

  const previousStep = useCallback((stepId: string) => {
    if (!serviceRef.current) return;

    const config = serviceRef.current.getConfig();
    const currentIndex = config.steps.findIndex(s => s.id === stepId);

    console.log(`[TutorialProvider] previousStep called with stepId: ${stepId}, currentIndex: ${currentIndex}`);

    if (currentIndex > 0) {
      // Move to previous step
      const prevStep = config.steps[currentIndex - 1];

      // Check if we're moving to a different task
      const currentTaskIndex = state.currentTaskIndex;
      let newTaskIndex = currentTaskIndex;

      // Check if the current step is an intro step for a task
      const taskIntroMatch = stepId.match(/^task-(\d+)-intro$/);

      if (taskIntroMatch) {
        // Moving back from a task intro step means going to the previous task
        const taskNumber = parseInt(taskIntroMatch[1], 10);
        newTaskIndex = taskNumber - 2; // Go to previous task (0-based index)
        console.log(`[TutorialProvider] Moving back from task intro to task ${newTaskIndex}`);
      }

      console.log(`[TutorialProvider] Transitioning from step ${stepId} to ${prevStep.id}, taskIndex: ${currentTaskIndex} -> ${newTaskIndex}`);

      setState(prev => ({
        ...prev,
        currentStepId: prevStep.id,
        currentTaskIndex: newTaskIndex
      }));
    }
  }, [state.currentTaskIndex]);

  const completeTutorial = useCallback(() => {
    const activeSoftware = (serviceRef.current?.getConfig().software || state.software) as
      | SoftwareType
      | '';

    if (!activeSoftware) {
      console.warn('[TutorialProvider] Attempted to complete tutorial without active software context');
      setState(prev => ({ ...prev, isActive: false }));
      return;
    }

    recordCompletion(activeSoftware);
    setState(prev => ({ ...prev, isActive: false }));
  }, [recordCompletion, state.software]);

  const exitTutorial = useCallback(() => {
    const activeSoftware = (serviceRef.current?.getConfig().software || state.software) as
      | SoftwareType
      | '';

    if (activeSoftware) {
      recordCompletion(activeSoftware);
    }

    setState(prev => ({ ...prev, isActive: false }));
    if (serviceRef.current) {
      serviceRef.current.clearCache();
    }
  }, [recordCompletion, state.software]);

  const getCurrentTask = useCallback((): TutorialTask | null => {
    return state.tasks[state.currentTaskIndex] || null;
  }, [state.tasks, state.currentTaskIndex]);

  const getCurrentTaskData = useCallback(() => {
    const currentTask = getCurrentTask();
    return currentTask ? state.taskData.get(currentTask.id) || null : null;
  }, [getCurrentTask, state.taskData]);

  const getProgress = useCallback(() => ({
    current: state.currentTaskIndex + 1,
    total: state.tasks.length,
  }), [state.currentTaskIndex, state.tasks]);

  const isLastTask = useCallback(() => {
    return state.currentTaskIndex >= state.tasks.length - 1;
  }, [state.currentTaskIndex, state.tasks]);

  const isFirstTask = useCallback(() => {
    return state.currentTaskIndex === 0;
  }, [state.currentTaskIndex]);

  const value: TutorialContextValue = {
    state,
    startTutorial,
    nextTask,
    previousTask,
    completeStep,
    previousStep,
    completeTutorial,
    exitTutorial,
    getCurrentTask,
    getCurrentTaskData,
    getProgress,
    isLastTask,
    isFirstTask,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
